/**
 * Top-of-screen route progress bar.
 *
 * The problem: React Router's <Link> doesn't give any visual feedback
 * between click and the new route painting. With lazy-loaded chunks,
 * that gap can be 2–3 seconds on the first visit — feels like the app
 * is hung.
 *
 * The fix: whenever the URL changes, animate a 2 px gradient bar across
 * the top. It hides itself once the new route mounts (Suspense resolves
 * and the location has settled).
 *
 * Implementation: we listen to location changes and start an animation.
 * The animation auto-completes on route-settled or 1.2 s, whichever comes
 * first — guarantees the bar doesn't linger if Suspense is fast.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export default function RouteProgress() {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef    = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevPath.current === pathname) return;
    prevPath.current = pathname;

    // Start new cycle.
    setVisible(true);
    setProgress(0);

    // Climb quickly to 60%, then linearly to 85% — never hits 100 until
    // the route has actually settled (see "settled" effect below).
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const elapsed = ts - start;
      // 0 → 60% in 180ms, 60 → 85% in 600ms, then idle.
      let v = 0;
      if (elapsed < 180)       v = (elapsed / 180) * 60;
      else if (elapsed < 780)  v = 60 + ((elapsed - 180) / 600) * 25;
      else                     v = 85;
      setProgress(v);
      if (elapsed < 780) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // Safety timer: force-close after 1.2s so a crashed Suspense never
    // leaves the bar mid-screen.
    timeoutRef.current = window.setTimeout(() => finish(), 1200);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [pathname]);

  // The "route settled" effect: a microtask after pathname change, we
  // know React rendered the new route (even if children Suspend, the
  // page shell is painted). Snap bar to 100% and fade it.
  useEffect(() => {
    if (!visible) return;
    // Let the route's Suspense fallback paint first, then close.
    const id = window.setTimeout(() => finish(), 260);
    return () => window.clearTimeout(id);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  function finish() {
    setProgress(100);
    window.setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 220);
  }

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        pointerEvents: "none",
        zIndex: 2147483646,
        opacity: visible ? 1 : 0,
        transition: "opacity 220ms var(--ease-smooth)",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${progress}%`,
          background: "var(--brand-gradient)",
          boxShadow: "0 0 12px 0 color-mix(in srgb, var(--accent-300) 55%, transparent)",
          transition: progress === 100
            ? "width 160ms var(--ease-smooth)"
            : "width 90ms linear",
        }}
      />
    </div>
  );
}
